console.log('[PTAH] animations v5.0');
/* =========================================================
   PTAH ANIMATIONS — GSAP · ScrollTrigger · Lenis · Mouse Follower
   Mobile-first (≈75% of traffic is phones). Heavy desktop-only
   effects (cursor follower, magnetic CTAs, spotlight glow) stay
   off on touch devices; everything respects prefers-reduced-motion.

   Load order: this file must run AFTER the GSAP / ScrollTrigger /
   Lenis / Mouse Follower CDN <script defer> tags in <head>, and
   AFTER the DOM is parsed (it also runs `defer`, so both hold).
   ========================================================= */
(function () {
  'use strict';

  /* ---------- Device & motion-preference detection ---------- */
  const isTouchDevice = ('ontouchstart' in window) ||
                        (navigator.maxTouchPoints > 0) ||
                        window.matchMedia('(hover: none), (pointer: coarse)').matches;
  const isMobile = window.matchMedia('(max-width: 768px)').matches;
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Shared namespace so later steps (and page-specific inline scripts)
  // can reuse the same detection + library instances without re-querying.
  const PTAH = (window.PTAH = window.PTAH || {});
  PTAH.isTouchDevice = isTouchDevice;
  PTAH.isMobile = isMobile;
  PTAH.prefersReducedMotion = prefersReducedMotion;

  /* ---------- GSAP + ScrollTrigger registration ---------- */
  const hasGSAP = typeof gsap !== 'undefined';
  const hasScrollTrigger = hasGSAP && typeof ScrollTrigger !== 'undefined';
  if (hasScrollTrigger) {
    gsap.registerPlugin(ScrollTrigger);
  }
  PTAH.hasGSAP = hasGSAP;
  PTAH.hasScrollTrigger = hasScrollTrigger;

  /* ---------- Lenis smooth scroll (site-wide) ---------- */
  // Native scrolling is always best on touch — Lenis only synthesizes
  // the desktop wheel/trackpad feel; smoothTouch stays OFF.
  let lenis = null;
  if (!prefersReducedMotion && typeof Lenis !== 'undefined') {
    lenis = new Lenis({
      duration: 0.8,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: 0.8,
      syncTouch: false,
      touchMultiplier: 2,
    });

    if (hasScrollTrigger) {
      // Lenis + GSAP ticker: единый тик, нет двойного advance.
      lenis.on('scroll', ScrollTrigger.update);
      gsap.ticker.add((time) => { lenis.raf(time * 1000); });
      gsap.ticker.lagSmoothing(0);
    } else {
      // ScrollTrigger недоступен — используем собственный rAF.
      const raf = (time) => { lenis.raf(time); requestAnimationFrame(raf); };
      requestAnimationFrame(raf);
    }
  }
  PTAH.lenis = lenis;

  // Intercept hash-anchor clicks so Lenis handles them, not the browser's
  // native scroll-behavior (which fires simultaneously and causes a jump).
  if (lenis) {
    document.querySelectorAll('a[href^="#"]').forEach(function(a) {
      a.addEventListener('click', function(e) {
        var href = this.getAttribute('href');
        if (!href || href === '#') return;
        var target = document.querySelector(href);
        if (target) { e.preventDefault(); lenis.scrollTo(target, { offset: 0 }); }
      });
    });
  }

  /* ---------- Effect 4: word-by-word "rise from below" title reveal ---------
     Splits headings into per-word spans (.word > .word-inner) that slide up
     out of an overflow-hidden mask, staggered via GSAP + ScrollTrigger.

     Deliberately SKIPS .hero__title: that heading already ships its own
     bespoke per-word reveal (.hero__line > .hw, "word-up" keyframes in
     styles.css with hand-tuned per-word delays). Re-splitting it here would
     blow away that markup *and* fight it for control of the same element. */
  (function initTextReveal() {
    const targets = document.querySelectorAll('.section-head__title, .cz-head__title, .page-head__title');
    if (!targets.length) return;

    // Recursively wrap text runs in .word > .word-inner while preserving any
    // existing inline markup (<em>, <br>, <strong> …). Several headings on
    // this site rely on <em> for the bronze accent and <br> for line breaks
    // (e.g. "Build it.<br><em>Preview it.</em> Order from 1.") — rebuilding
    // from `textContent` the way the spec snippet does would silently strip
    // both. Walking the DOM keeps every wrapper element intact.
    function splitNode(node) {
      Array.from(node.childNodes).forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          if (!child.textContent || !child.textContent.trim()) return;
          const frag = document.createDocumentFragment();
          child.textContent.split(/(\s+)/).forEach((tok) => {
            if (tok === '') return;
            if (/^\s+$/.test(tok)) { frag.appendChild(document.createTextNode(tok)); return; }
            const outer = document.createElement('span');
            outer.className = 'word';
            outer.style.cssText = 'display:inline-block;overflow:hidden;';
            const inner = document.createElement('span');
            inner.className = 'word-inner';
            inner.style.cssText = 'display:inline-block;';
            inner.textContent = tok;
            if (!prefersReducedMotion) inner.style.transform = 'translateY(100%)';
            outer.appendChild(inner);
            frag.appendChild(outer);
          });
          node.replaceChild(frag, child);
        } else if (child.nodeType === Node.ELEMENT_NODE && child.tagName !== 'BR') {
          splitNode(child); // recurse into <em>/<strong>/etc. — keeps the wrapper, splits its text
        }
      });
    }

    targets.forEach((el) => {
      splitNode(el);
      const inners = el.querySelectorAll('.word-inner');
      if (!inners.length) return;

      if (prefersReducedMotion || !hasGSAP || !hasScrollTrigger) {
        inners.forEach((w) => { w.style.transform = 'translateY(0)'; });
        return;
      }
      gsap.to(inners, {
        y: 0,
        duration: 0.9,
        ease: 'expo.out',
        stagger: 0.06,
        scrollTrigger: { trigger: el, start: 'top 85%', once: true }
      });
    });
  })();

  /* ---------- Effect 9: page-enter stagger reveal ---------------------------
     Spec asked to hide the whole <body> (opacity:0) until JS marks it loaded.
     We deliberately DON'T do that part: it would add a brand-new failure mode
     where a blocked/404'd script leaves the entire site permanently blank —
     a much bigger blast radius than anything currently in this codebase.
     Instead we reveal only elements that opt in via [data-page-reveal],
     which mirrors the existing [data-reveal] system's (already-accepted)
     risk profile, and skip the spots that already animate in on their own
     (.hero__* — see initTextReveal note above — and product.html's
     .cz-head__row, which already carries [data-reveal]). */
  (function initPageReveal() {
    const els = document.querySelectorAll('[data-page-reveal]');
    if (!els.length) return;
    els.forEach((el, i) => { el.style.transitionDelay = (i * 0.08) + 's'; });
    // setTimeout, not rAF: rAF never fires in throttled/background tabs,
    // which left every [data-page-reveal] element (incl. the header)
    // stuck at opacity 0 until the tab was focused.
    setTimeout(() => { document.body.classList.add('is-loaded'); }, 16);
  })();

  /* ---------- Effect 6: burger -> X morph, synced to drawer state ---------
     The visual (3 bars -> X, staggered link reveal) is pure CSS in
     styles.css, driven by .header__menu-btn.is-active / .drawer.is-open.
     All this does is keep .is-active mirroring #mobile-menu's EXISTING
     .is-open state via MutationObserver -- so the icon stays correct no
     matter how the drawer opens or closes (click, overlay click, Esc key,
     future code paths...) without touching app.js's bindDrawer(), which
     both the cart AND menu drawers share. Also flips aria-expanded /
     aria-label for screen readers, mirroring the drawer__close button's
     existing "Close menu" wording. */
  (function initBurgerMorph() {
    const btn = document.querySelector('.header__menu-btn');
    const drawer = document.getElementById('mobile-menu');
    if (!btn || !drawer) return;
    const sync = () => {
      const open = drawer.classList.contains('is-open');
      btn.classList.toggle('is-active', open);
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      btn.setAttribute('aria-label', open ? 'Close menu' : 'Menu');
    };
    sync();
    new MutationObserver(sync).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  })();

  /* ---------- Effect 1: cinematic hero — Cascade-style framed reveal ---------
     Visitor lands on a small centred portrait window showing the hero photo
     ZOOMED IN (scale 1.7 — close-up, only the centre of the image visible).
     As they scroll the pinned 100vh/100vh range:
       0–100% : clip-path expands to fullscreen (window grows outward)
       0–100% : image transform scales from 1.7 down to 1.0 (camera pulls back —
                more of the photo becomes visible as the window grows; the
                two effects compound for a real "stage opening" feel)
       15–35% : eyebrow fades up into place
       30–55% : title fades up (per-word reveal inside the title is handled
                by initTextReveal — this one drives the wrapper's opacity)
       50–75% : subtitle fades up
       70–90% : CTA + meta fade up
       95–100%: scroll hint fades out (it stayed visible the whole time as
                a "keep scrolling" affordance, like Cascade & Coal does)

     All timings are scrubbed (continuously tied to scroll position), so
     scrolling back UP reverses the reveal — no one-shot, fully reversible.

     IMG element (not <video>) — replaces the prior video tag in index.html
     because Rostik supplied a stills-first hero (video comes later, will
     drop in as <video class="hero__img"> with the same scale tween). */
  (function initHeroClipReveal() {
    if (!hasGSAP || !hasScrollTrigger || prefersReducedMotion) {
      document.body.classList.add('hero-lines-ready');
      return;
    }
    const heroSection = document.querySelector('.hero--cinematic');
    const heroMediaWrap = document.querySelector('[data-hero-clip]');
    const heroImg = document.querySelector('.hero--cinematic .hero__img');
    if (!heroSection || !heroMediaWrap) {
      document.body.classList.add('hero-lines-ready');
      return;
    }
    const heroHint = document.querySelector('[data-hero-hint]');

    // Track 1 — full pinned range: clip-path expands to fullscreen
    gsap.to(heroMediaWrap, {
      clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)',
      ease: 'none',
      scrollTrigger: { trigger: heroSection, start: 'top top', end: 'bottom bottom', scrub: 1 }
    });

    // Track 2 — обратный зум: рамка расширяется, а камера НЫРЯЕТ
    // в вышивку — фото приближается. Зум адаптивный: на десктопе
    // (широкое окно-полоса) старт 1.7 → 1.15; на мобиле портретное
    // окно узкое: 1.0 → 1.15. Ранее 1.28 — 'p' уходила за левый
    // край при object-position 48%; снижено до 1.15 вместе со
    // сдвигом object-position → 40% (v4.5).
    if (heroImg) {
      var heroIsMobile = window.matchMedia('(max-width: 768px)').matches;

      // Desktop keeps the original zoom-in (1.7 → 1.15, centred).
      var zFrom = 1.7, zTo = 1.15, yOffD = '0%';
      if (heroIsMobile) {
        // ── Мокап Ростика (CapCut, кадр 1 → кадр 2, канва = кадр видео 16:9):
        // стартовое окно ПОЧТИ КВАДРАТ (аспект кропа 0.946) и показывает
        // область видео 43.6% × 82% с центром (56.6%, 57.4%).
        //
        // КЛЮЧЕВОЕ: object-fit:cover обрезает бока 16:9-кадра ПО БОКСУ
        // элемента ещё до transform — правая часть кадра исчезала
        // безвозвратно («полосы» на телефоне). Поэтому боксу видео явно
        // задаём РАЗМЕР САМОГО КАДРА (Vh·A × Vh, по центру) — теперь
        // transform двигает весь кадр целиком, ничего не отрезано.
        var A     = 854 / 480;               // аспект hero.mp4
        // CROPCX 0.566 → 0.535: по замеру кадра логотип начинался на
        // x≈0.32, левый край кропа 0.348 резал глиф 'd' — сдвигаем окно
        // чуть левее, логотип целиком в стартовом кадре.
        var CROPW = 0.436, CROPCX = 0.535, CROPCY = 0.574;
        // Размер — у стейджа (100vw × 100lvh): innerHeight на iOS «дышит».
        var _wr = heroMediaWrap.getBoundingClientRect();
        var Vw = _wr.width  || window.innerWidth;
        var Vh = _wr.height || window.innerHeight;

        // Бокс видео = полный кадр, центрированный на стейдже
        var elW = Vh * A, elH = Vh;
        heroImg.style.position = 'absolute';
        heroImg.style.inset = 'auto';
        heroImg.style.width  = elW.toFixed(1) + 'px';
        heroImg.style.height = elH.toFixed(1) + 'px';
        heroImg.style.left   = ((Vw - elW) / 2).toFixed(1) + 'px';
        heroImg.style.top    = '0px';
        heroImg.style.maxWidth = 'none';
        heroImg.style.objectFit = 'fill'; // бокс уже в аспекте кадра

        // Окно: 80vw, аспект = аспекту кропа
        var winWpx = 0.80 * Vw;
        var winHpx = winWpx / ((CROPW * A) / 0.82);
        var yHalf  = winHpx / 2 / Vh * 100;
        var yTop = (50 - yHalf).toFixed(2), yBot = (50 + yHalf).toFixed(2);
        heroMediaWrap.style.clipPath =
          'polygon(10% ' + yTop + '%, 90% ' + yTop + '%, 90% ' + yBot + '%, 10% ' + yBot + '%)';
        var frameEl = document.querySelector('.hero__media-frame');
        if (frameEl) frameEl.style.inset = (50 - yHalf + 1.6).toFixed(2) + '% 11.5%';

        // Транформы кадра (px, origin = центр бокса = центр экрана):
        var vidWpx = winWpx / CROPW;         // ширина всего кадра на экране (старт)
        var sStart = vidWpx / elW;
        var txPx   = -(CROPCX - 0.5) * vidWpx;
        var tyPx   = -(CROPCY - 0.5) * (vidWpx / A);
        // Финал скролла: кадр покрывает весь стейдж (высота = Vh при scale 1)
        gsap.fromTo(heroImg,
          { scale: sStart, x: txPx, y: tyPx },
          {
            scale: 1, x: 0, y: 0,
            ease: 'none',
            scrollTrigger: { trigger: heroSection, start: 'top top', end: 'bottom bottom', scrub: 1 }
          }
        );
      } else {
        gsap.fromTo(heroImg,
          { scale: zFrom, y: yOffD },
          {
            scale: zTo, y: yOffD,
            ease: 'none',
            scrollTrigger: { trigger: heroSection, start: 'top top', end: 'bottom bottom', scrub: 1 }
          }
        );
      }
    }

    // Track 3 — staged copy reveal at specific scroll percentages.
    // Each tween has its OWN ScrollTrigger range (start/end as % of the
    // pinned section), so they fire at the right scroll point regardless
    // of viewport height. `toggleActions` plays/reverses around each range.
    function stage(selector, startPct, endPct) {
      const el = document.querySelector(`[data-hero-stage="${selector}"]`);
      if (!el) return;
      gsap.to(el, {
        opacity: 1,
        y: 0,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: heroSection,
          start: `${startPct}% top`,
          end: `${endPct}% top`,
          scrub: 0.6
        }
      });
    }
    // Clean intro: шапка и notice скрыты (CSS body.hero-intro) и
    // въезжают на 55–78% скролла. Высота notice — в CSS-переменную,
    // чтобы fixed-header встал ровно под ним на любом экране.
    document.body.classList.add('hero-intro');
    var noticeEl = document.querySelector('.notice');
    if (noticeEl) document.body.style.setProperty('--nh', noticeEl.offsetHeight + 'px');
    gsap.to(['.notice', '.header'], {
      opacity: 1, y: 0, pointerEvents: 'auto', ease: 'power2.out',
      scrollTrigger: { trigger: heroSection, start: '55% top', end: '78% top', scrub: 0.6 }
    });

    stage('eyebrow', 14, 28);
    stage('sub',     48, 66);
    stage('cta',     62, 82);
    stage('meta',    72, 88);

    // Title line-mask reveal: каждая строка выезжает из своей маски
    // (yPercent 115 → 0), слова внутри строки — лёгким стаггером.
    // Первая строка на 25–42%, вторая на 33–52% скролла. Scrub —
    // обратимо при скролле вверх, как и всё остальное.
    // Title reveal moved to inline script in index.html (self-contained,
    // no GSAP dependency) — see "PTAH hero title reveal" there.

    // Track 4 — scroll hint fades EARLY (18→38%): как только посетитель
    // начал листать и контент пошёл раскрываться, подсказка свою работу
    // сделала и плавно уходит — иначе «остаётся по итогу» на раскрытом
    // экране, что выглядит как недоработка.
    if (heroHint) {
      gsap.to(heroHint, {
        opacity: 0,
        y: -8,
        ease: 'none',
        scrollTrigger: { trigger: heroSection, start: '18% top', end: '38% top', scrub: true }
      });
    }

    // Sticky-chat появляется после того как hero полностью раскрылся.
    // Используем onUpdate с progress (0→1) — работает на любом экране
    // без пересчёта процентов по высоте секции.
    // progress 0.88→0.98 = последние 12% hero-анимации → плавное появление.
    var stickyChat = document.querySelector('.sticky-chat');
    if (stickyChat) {
      gsap.set(stickyChat, { opacity: 0, pointerEvents: 'none' });
      ScrollTrigger.create({
        trigger: heroSection,
        start: 'top top',
        end: 'bottom bottom',
        onUpdate: function (self) {
          var p = Math.max(0, Math.min(1, (self.progress - 0.88) / 0.10));
          gsap.set(stickyChat, { opacity: p, pointerEvents: p > 0.5 ? 'auto' : 'none' });
        }
      });
    }

    // Мобильные браузеры меняют высоту вьюпорта при скрытии адресной
    // строки — это сбивает расчёт диапазона sticky/scrub. Пересчитываем
    // ScrollTrigger после полной загрузки и при заметном ресайзе, чтобы
    // зум/раскрытие стартовали точно с первого пикселя прокрутки.
    window.addEventListener('load', function () {
      if (window.ScrollTrigger) ScrollTrigger.refresh();
    });
    var heroVW = window.innerWidth, heroRT;
    window.addEventListener('resize', function () {
      clearTimeout(heroRT);
      heroRT = setTimeout(function () {
        // игнорируем «дрожание» высоты от адресной строки на мобиле —
        // рефрешим только при реальной смене ширины
        if (Math.abs(window.innerWidth - heroVW) < 40) return;
        heroVW = window.innerWidth;
        if (window.ScrollTrigger) ScrollTrigger.refresh();
      }, 200);
    });
  })();

  /* ---------- Hero video pause-while-offscreen: removed in v3 ---------
     The hero element is now an <img> (Rostik supplied a stills-first hero;
     the photo of the embroidered "ptah" hoodie + craftsman's hand). When
     the video is ready it will drop back in as <video class="hero__img">
     and this guard can be reinstated. For now an image has no playback to
     manage — IntersectionObserver here was a no-op without a <video> tag,
     so deleting the dead code is safer than leaving a stub. */

  /* ---------- Effect 8: Desktop cursor follower + spotlight glow ---------
     Creates two elements via JS (never pollutes touch-device DOM):
       .cursor-dot  — small bronze dot, tight lerp (0.14), follows closely
       .cursor-glow — wide radial glow, heavy lerp (0.05), lags beautifully
     Expands dot into hollow ring over links/buttons.
     body.has-cursor suppresses the native cursor (see styles.css). */
  (function initMouseFollower() {
    if (isTouchDevice || prefersReducedMotion) return;

    document.body.classList.add('has-cursor');

    var dot  = document.createElement('div');
    dot.className = 'cursor-dot';
    document.body.appendChild(dot);

    var glow = document.createElement('div');
    glow.className = 'cursor-glow';
    document.body.appendChild(glow);

    var mx = -999, my = -999;
    var dx = -999, dy = -999;
    var gx = -999, gy = -999;
    var entered = false;

    var INTERACTIVE = 'a,button,label,input,select,textarea,[role="button"],[data-garment],[data-type],[data-lightbox]';

    document.addEventListener('mousemove', function(e) {
      mx = e.clientX;
      my = e.clientY;
      if (!entered) { dx = mx; dy = my; gx = mx; gy = my; entered = true; }
      dot.classList.add('is-visible');
      glow.classList.add('is-visible');

      // ring mode when over interactive elements
      var over = e.target.closest(INTERACTIVE);
      dot.classList.toggle('is-ring', !!over);
    }, { passive: true });

    document.addEventListener('mouseleave', function() {
      dot.classList.remove('is-visible');
      glow.classList.remove('is-visible');
    });

    (function raf() {
      dx += (mx - dx) * 0.14;
      dy += (my - dy) * 0.14;
      dot.style.transform  = 'translate(' + dx.toFixed(1) + 'px,' + dy.toFixed(1) + 'px)';
      gx += (mx - gx) * 0.05;
      gy += (my - gy) * 0.05;
      glow.style.transform = 'translate(' + gx.toFixed(1) + 'px,' + gy.toFixed(1) + 'px)';
      requestAnimationFrame(raf);
    })();
  })();

  /* ---------- Effect 7: marquee speed tied to scroll velocity ----------
     Marquee runs at BASE duration normally; each scroll event pushes the
     target duration toward FAST proportional to delta px, then idles back
     after 900 ms. A lightweight rAF loop lerps `cur` toward `tgt` for a
     smooth elastic feel — no jump. Compatible with CSS hover-pause and
     prefers-reduced-motion (guarded above). */
  (function initMarquee() {
    if (prefersReducedMotion) return;
    var track = document.querySelector('.marquee__track');
    if (!track) return;
    track.style.animationDuration = '35s';
  })();

  /* ---------- Lightbox for [data-lightbox] images ---------- */
  (function initLightbox() {
    document.addEventListener('click', function(e) {
      var trigger = e.target.closest('[data-lightbox]');
      if (!trigger) return;
      var src = trigger.tagName === 'IMG' ? trigger.src : (trigger.querySelector('img') || {}).src;
      if (!src) return;
      var ov = document.createElement('div');
      ov.className = 'lightbox-overlay';
      var full = document.createElement('img');
      full.src = src;
      ov.appendChild(full);
      function close() { ov.remove(); document.removeEventListener('keydown', onKey); }
      function onKey(ev) { if (ev.key === 'Escape') close(); }
      ov.addEventListener('click', close);
      document.addEventListener('keydown', onKey);
      document.body.appendChild(ov);
    });
  })();

})();
